import React, { FunctionComponent, useState, useMemo, useEffect } from "react"
import { useTranslation } from "react-i18next"

import SearchLocation from "components/SearchLocation/SearchLocation"
import Input from "components/Input/Input"
import CustomError from "models/CustomError"
import ValidationService, { validationRules } from "services/ValidationService"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import Form from "components/Form/Form"
import FormField from "components/FormField/FormField"
import { Coordinates, IValidationRule } from "@/types"
import { Grid, GridItem } from "components/Grid"
import Address from "models/Address"
import { MapMarkerType } from "components/MapView/MapMarker/MapMarker"
import LocationService from "services/LocationService"
import toastMessages from "stores/toastMessages"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"

export interface ILocationAddressModalProps {
    title?: string
    description?: string
    coordinates?: Coordinates | null
    address?: Address | null
    coordinatesRequired?: boolean
    addressRequired?: boolean
    descriptionError?: string | null
    onClose(): void
    onSave(description: string, coordinates: Coordinates | null, address: Address): Promise<void> | void
}

const LocationAddressModal: FunctionComponent<React.PropsWithChildren<ILocationAddressModalProps>> = (props) => {
    const { t } = useTranslation()
    const [isSubmitted, setIsSumbitted] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const { title, coordinatesRequired, addressRequired, onSave, onClose } = props

    const [description, setDescription] = useState(props.description || "")
    const [descriptionError, setDescriptionError] = useState(props.descriptionError || "")
    const [coordinates, setCoordinates] = useState<Coordinates | null>(props.coordinates || null)
    const [street, setStreet] = useState(props.address?.street || "")
    const [zip, setZip] = useState(props.address?.zip || "")
    const [city, setCity] = useState(props.address?.city || "")
    const [countryName, setCountryName] = useState(props.address?.countryName || "")

    useEffect(() => {
        setDescriptionError(props.descriptionError || "")
    }, [props.descriptionError])

    const validationErrors = useMemo((): { [x: string]: CustomError } => {
        let validationFields: [string, IValidationRule, any][] = [
            ["description", validationRules.location.description, description],
        ]

        if (coordinatesRequired) {
            validationFields.push(["coordinates", { required: true }, coordinates])
        }

        if (addressRequired) {
            const addressValidationFields: [string, IValidationRule, any][] = [
                ["street", { required: true }, street],
                ["zip", { required: true }, zip],
                ["city", { required: true }, city],
                ["countryName", { required: true }, countryName],
            ]
            validationFields = [
                ...validationFields,
                ...addressValidationFields,
            ]
        }
        return ValidationService.validateMultiple(validationFields)
    }, [addressRequired, city, coordinates, coordinatesRequired, countryName, description, street, zip])

    function handleDescriptionChange(newDescription: string) {
        setDescriptionError("")
        setDescription(newDescription)
    }

    function clearAddress() {
        setStreet("")
        setZip("")
        setCity("")
        setCountryName("")
    }

    async function handleCoordinatesChange(newCoordinates: Coordinates | null) {
        setCoordinates(newCoordinates)
        if (!newCoordinates){
            return
        }
        const { address: foundAddress } = await LocationService.getAddressByCoordinates(newCoordinates)
        if (!foundAddress) {
            clearAddress()
            return
        }
        setStreet(foundAddress.street || "")
        setZip(foundAddress.zip || "")
        setCity(foundAddress.city || "")
        setCountryName(foundAddress.countryName || "")
    }


    async function handleSubmit() {
        setIsSumbitted(true)
        if (Object.keys(validationErrors).length !== 0
            || descriptionError) {
            return
        }

        setIsSubmitting(true)
        try {
            await onSave(description, coordinates, new Address({
                street,
                zip,
                city,
                countryName
            }))
        } catch(e) {
            toastMessages.showSystemError()
            setIsSubmitting(false)
        }
        setIsSubmitting(false)
    }

    return (
        <>
            <ConfirmModal
                title={title ?? t('location.selectLocation')}
                onClose={onClose}
                maxWidth="586px"
                confirmButtonLabel={t('common.save')}
                isBusy={isSubmitting}
                onConfirm={handleSubmit}>
                <Form onSubmit={handleSubmit}>
                    <FormField
                        className="mb-3"
                        label={t('location.locationDescription')}
                        helpTooltip={{
                            content: t('location.locationDescriptionTooltip')
                        }}>
                        <Input
                            value={description}
                            onChange={e => handleDescriptionChange(e.target.value)}
                            isInvalid={!!descriptionError || (isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'description'))}
                            name="description" />
                        {(!!descriptionError || (isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'description'))) &&
                            <ValidationMessage>{descriptionError || validationErrors.description.message}</ValidationMessage>
                        }
                    </FormField>
                    <SearchLocation
                        searchFieldLabel={t('location.location')}
                        onChangeMarkerCoordinates={handleCoordinatesChange}
                        initMarkerCoordinates={coordinates}
                        markerType={MapMarkerType.target}
                        mapHeight="220px"
                        isEditable
                    />
                    {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'coordinates') &&
                        <ValidationMessage>{validationErrors.coordinates.message}</ValidationMessage>
                    }
                    <FormField
                        className="mt-3"
                        optional={!addressRequired}
                        label={t('address.address')}>
                        <Input
                            onChange={e => setStreet(e.target.value)}
                            value={street}
                            name="street"
                            placeholder={t('address.street')}
                            isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'street')}
                        />
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'street') &&
                            <ValidationMessage>{validationErrors.street.message}</ValidationMessage>
                        }
                    </FormField>
                    <Grid
                        className="mt-3"
                        columns="1fr 2fr"
                        gap="2.4rem">
                        <GridItem>
                            <FormField
                                optional={!addressRequired}>
                                <Input
                                    onChange={e => setZip(e.target.value)}
                                    value={zip}
                                    name="zip"
                                    placeholder={t('address.zip')}
                                    isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'zip')}
                                />
                                {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'zip') &&
                                    <ValidationMessage>{validationErrors.zip.message}</ValidationMessage>
                                }
                            </FormField>
                        </GridItem>
                        <GridItem>
                            <FormField
                                optional={!addressRequired}>
                                <Input
                                    onChange={e => setCity(e.target.value)}
                                    value={city}
                                    name="city"
                                    placeholder={t('address.city')}
                                    isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'city')}
                                />
                                {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'city') &&
                                    <ValidationMessage>{validationErrors.city.message}</ValidationMessage>
                                }
                            </FormField>
                        </GridItem>
                    </Grid>
                    <FormField
                        className="mt-3"
                        optional={!addressRequired}>
                        <Input
                            onChange={e => setCountryName(e.target.value)}
                            value={countryName}
                            name="country"
                            placeholder={t('address.country')}
                            isInvalid={isSubmitted && ValidationService.isFieldInvalid(validationErrors, 'countryName')}
                        />
                        {isSubmitted && ValidationService.hasFieldErrorMessage(validationErrors, 'countryName') &&
                            <ValidationMessage>{validationErrors.countryName.message}</ValidationMessage>
                        }
                    </FormField>
                </Form>
            </ConfirmModal>
        </>
    )
}

export default LocationAddressModal
